import React, { useMemo, useState } from 'react';
import { DashboardAPIResponse, WorkflowDetailedChartsProps } from 'src/pages/dashboard/Dashboard.interface';
import { boardI18nStrings, boardItemI18nStrings, xTickFormatter, yTickFormatter, REGION_BAR_COLOR, COUNTRY_BAR_COLOR } from 'src/components/dashboard/DashboardConstants';
import { buildStatusPieChartData, mergeCountsByKey } from 'src/components/dashboard/DashboardUtils';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { SNOW_DATA_SERVICE_ENDPOINT } from 'src/config';
import Header from '@amzn/awsui-components-console/header';
import Spinner from '@amzn/awsui-components-console/spinner';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import PieChart from '@amzn/awsui-components-console/pie-chart';
import BarChart from '@amzn/awsui-components-console/bar-chart';
import Board, { BoardProps } from '@amzn/awsui-board-components-console/board';
import BoardItem from '@amzn/awsui-board-components-console/board-item';
import EmptyState from 'src/components/commonComponents/EmptyState/EmptyState';

interface BoardItemData {
	title: string;
	chartId: 'status' | 'region' | 'awsRegion' | 'country';
}

const createBoardItem = (
	id: string,
	title: string,
	chartId: BoardItemData['chartId'],
	columnOffset: number,
): BoardProps.Item<BoardItemData> => ({
	id,
	definition: { defaultRowSpan: 5, defaultColumnSpan: 2, minRowSpan: 3, minColumnSpan: 2 },
	columnOffset: { 4: columnOffset },
	rowSpan: 5,
	columnSpan: 2,
	data: { title, chartId },
});

const BOARD_ITEMS: BoardProps.Item<BoardItemData>[] = [
	createBoardItem('status-chart', 'Status Distribution', 'status', 0),
	createBoardItem('region-chart', 'Region Distribution', 'region', 2),
	createBoardItem('aws-region-chart', 'AWS Region Distribution', 'awsRegion', 0),
	createBoardItem('country-chart', 'Country Distribution', 'country', 2),
];

export default function WorkflowDetailedCharts({ year, workflowTypes, month, workflowNameMap }: WorkflowDetailedChartsProps) {
	const queryParams = useMemo(() => {
		const params: Record<string, string> = { year, workflowTypes: workflowTypes.join(',') };
		if (month) params.month = month;
		return params;
	}, [year, workflowTypes, month]);

	const { isLoading, data: response, error } = useFetchForSDS<DashboardAPIResponse>({
		path: '/dashboard/workflows', method: 'GET', queryParams, url: SNOW_DATA_SERVICE_ENDPOINT,
	});

	const data = useMemo(() => {
		if (!response?.items?.length) return null;
		if (response.items.length === 1) return response.items[0];
		const allCharts = response.items.map((item) => item.charts);
		return {
			...response.items[0],
			charts: {
				byStatus: mergeCountsByKey(allCharts.flatMap((charts) => charts.byStatus || []), 'status'),
				byRegion: mergeCountsByKey(allCharts.flatMap((charts) => charts.byRegion || []), 'region'),
				byAwsRegion: mergeCountsByKey(allCharts.flatMap((charts) => charts.byAwsRegion || []), 'awsRegion'),
				byCountry: mergeCountsByKey(allCharts.flatMap((charts) => charts.byCountry || []), 'country'),
			},
		};
	}, [response]);

	const { pieData: statusChartData, total: totalWorkflows } = useMemo(
		() => data ? buildStatusPieChartData([data]) : { pieData: [], total: 0 }, [data]);
		
	const totalAwsRegions = useMemo(
		() => data?.charts.byAwsRegion?.reduce((sum, region) => sum + region.count, 0) ?? 0, [data]);

	const workflowLabel = useMemo(() =>
		workflowTypes.map((id) => workflowNameMap?.[id] || id).join(', '), [workflowTypes, workflowNameMap]);

	const [boardItems, setBoardItems] = useState(BOARD_ITEMS);

	const renderChart = (chartId: string) => {
		if (isLoading) return <Box textAlign="center" padding={{ vertical: 'xxl' }}><Spinner size="large" /></Box>;
		if (error) return <Alert type="error" header="Error loading data">{error}</Alert>;
		if (!data) return <Alert type="info">No data available for this workflow.</Alert>;

		switch (chartId) {
			case 'status':
				return (
					<PieChart data={statusChartData}
						detailPopoverContent={(segment, total) => [
							{ key: 'Status', value: segment.title }, { key: 'Count', value: segment.value },
							{ key: 'Percentage', value: `${((segment.value / total) * 100).toFixed(1)}%` },
						]}
						detailPopoverSize="large" innerMetricDescription="workflows" innerMetricValue={totalWorkflows.toString()}
						variant="donut" size="large" fitHeight hideFilter legendTitle="Workflow Status"
						ariaLabel="Status distribution"
						empty={<EmptyState title="No status data available for the selected period." />}
					/>
				);
			case 'region':
				return (
					<BarChart
						series={[{ title: 'Workflows', type: 'bar', data: (data.charts.byRegion || []).map((region) => ({ x: region.region, y: region.count })), color: REGION_BAR_COLOR }]}
						xDomain={(data.charts.byRegion || []).map((region) => region.region)}
						yTitle="Count" xTitle="Region" height={350} fitHeight hideFilter hideLegend emphasizeBaselineAxis
						i18nStrings={{ xTickFormatter, yTickFormatter }} ariaLabel="Region distribution"
						empty={<EmptyState title="No region data available for the selected period." />}
					/>
				);
			case 'awsRegion':
				return (
					<PieChart
						data={(data.charts.byAwsRegion || []).map((region) => ({ title: region.awsRegion, value: region.count }))}
						detailPopoverContent={(segment, total) => [
							{ key: 'AWS Region', value: segment.title }, { key: 'Count', value: segment.value },
							{ key: 'Percentage', value: `${((segment.value / total) * 100).toFixed(1)}%` },
						]}
						detailPopoverSize="large" innerMetricDescription="workflows" innerMetricValue={totalAwsRegions.toString()}
						variant="donut" size="large" fitHeight hideFilter legendTitle="AWS Regions"
						ariaLabel="AWS region distribution"
						empty={<EmptyState title="No AWS region data available for the selected period." />}
					/>
				);
			case 'country':
				return (
					<BarChart
						series={[{ title: 'Workflows', type: 'bar', data: (data.charts.byCountry || []).map((country) => ({ x: country.country, y: country.count })), color: COUNTRY_BAR_COLOR }]}
						xDomain={(data.charts.byCountry || []).map((country) => country.country)}
						yTitle="Count" xTitle="Country" height={350} fitHeight hideFilter hideLegend emphasizeBaselineAxis
						i18nStrings={{ xTickFormatter, yTickFormatter }} ariaLabel="Country distribution"
						empty={<EmptyState title="No country data available for the selected period." />}
					/>
				);
			default: return null;
		}
	};

	return (
		<Board<BoardItemData>
			items={boardItems}
			onItemsChange={({ detail }) => setBoardItems(detail.items as BoardProps.Item<BoardItemData>[])}
			i18nStrings={boardI18nStrings}
			empty={<EmptyState title="No widgets available" />}
			renderItem={(item) => (
				<BoardItem
					header={
						<Header
							description={`Workflow: ${workflowLabel} | Period: ${month ? `${year}-${month}` : year}`}
						>
							{item.data?.title}
						</Header>
					}
					i18nStrings={boardItemI18nStrings}
				>
					{renderChart(item.data?.chartId || '')}
				</BoardItem>
			)}
		/>
	);
}
